import { useRef, useState } from 'react'
import { FileText, Loader2, Upload } from 'lucide-react'
import { openFilePreview } from '@/lib/utils/filePreview'
import { showError } from '@/lib/utils/notifications'
import type { ChargebackDetailUploadSelection } from '../../types/pages'
import type {
  ChargebackFileTypeChoice,
  ChargebackFileUploadSectionProps,
} from '../../types/components'
import type { ChargebackFileAttachment } from '../../types/api'
import {
  ALL_DECISION_FILE_TYPES,
  CHARGEBACK_DETAIL_LABEL_CLASS,
  CHARGEBACK_FILE_MAX_SIZE_MB,
  CHARGEBACK_FILE_TYPE_OTHER,
} from '../../constants/chargebackDetailConstants'
import { display, formatDateTime } from '../../utils/chargebackDetailFormatters'
import { ChargebackConfirmModal } from './ChargebackConfirmModal'
import { ChargebackFileTypeSelectionModal } from './ChargebackFileTypeSelectionModal'

export function ChargebackFileUploadSection({
  title,
  fieldName,
  fileTypes,
  uploadedType,
  detail,
  applicantId,
  disabled,
  busy,
  onUpload,
  onDelete,
}: ChargebackFileUploadSectionProps) {
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [typeModalOpen, setTypeModalOpen] = useState(false)
  const [confirmSelections, setConfirmSelections] = useState<
    ChargebackDetailUploadSelection[]
  >([])
  const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const uploaded = Array.isArray(detail?.chargeback_files?.[uploadedType])
    ? detail.chargeback_files[uploadedType]
    : []

  const resetSelection = () => {
    setPendingFiles([])
    setConfirmSelections([])
    setTypeModalOpen(false)
    setUploadConfirmOpen(false)
  }

  const handleFileChange = (fileList: FileList | null) => {
    const files = fileList ? Array.from(fileList) : []
    // Clear the input so re-picking the same file still fires `change`.
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!files.length) {
      resetSelection()
      return
    }
    if (files.some((file) => file.size / 1024 / 1024 > CHARGEBACK_FILE_MAX_SIZE_MB)) {
      showError('File Size Exceeds Allowable Limit of 5 MB.')
      resetSelection()
      return
    }
    setConfirmSelections([])
    setPendingFiles(files)
    setTypeModalOpen(true)
  }

  const handleTypesChosen = (choices: ChargebackFileTypeChoice[]) => {
    setConfirmSelections(
      pendingFiles.map((file, index) => {
        const choice = choices[index]
        const isOther = choice?.checkType === CHARGEBACK_FILE_TYPE_OTHER
        return {
          file,
          checkType: isOther ? choice.otherValue.trim() : (choice?.checkType ?? ''),
        }
      }),
    )
    setTypeModalOpen(false)
    setUploadConfirmOpen(true)
  }

  const fileLabel = pendingFiles.map((file) => file.name).join(', ') || 'Choose files to upload'

  const confirmUpload = async () => {
    const selections = confirmSelections
    setUploadConfirmOpen(false)
    await onUpload(fieldName, selections)
    resetSelection()
  }
  const openFilePicker = () => fileInputRef.current?.click()

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>{title}</span>
        <div className="flex min-h-[2.5rem] w-full max-w-xl items-center justify-between rounded-lg border border-slate-200 bg-white p-1 pl-3 shadow-sm transition-all focus-within:border-[#1597ff] focus-within:ring-1 focus-within:ring-[#1597ff]">
          <button
            type="button"
            disabled={disabled || busy}
            onClick={openFilePicker}
            className={`flex min-w-0 flex-1 items-center gap-2 truncate text-left text-sm transition ${
              pendingFiles.length ? 'text-slate-800 font-medium' : 'text-slate-500'
            } hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60`}
            aria-label={pendingFiles.length ? `Selected: ${fileLabel}` : 'Choose file'}
          >
            <Upload className="h-4 w-4 shrink-0 text-[#1597ff]" aria-hidden />
            <span className="truncate">{fileLabel}</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            disabled={disabled || busy}
            onChange={(event) => handleFileChange(event.target.files)}
            className="sr-only"
          />
          <button
            type="button"
            disabled={disabled || busy}
            onClick={openFilePicker}
            className="inline-flex shrink-0 items-center justify-center rounded-md border border-[#1597ff] bg-white px-4 py-1.5 text-sm font-semibold text-[#1597ff] shadow-sm transition hover:bg-blue-50/50 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Choose files to upload"
          >
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Upload
          </button>
        </div>
      </div>

      {uploaded.length ? (
        <div className="space-y-2 pt-1">
          <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Files uploaded</span>
          <div className="space-y-2">
            {uploaded.map((file: ChargebackFileAttachment, index: number) => (
              <div
                key={`${file.attachment_id ?? file.filename}-${index}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-[#f8fafc]/60 p-2.5 transition-colors hover:bg-[#f8fafc]"
              >
                <button
                  type="button"
                  className="inline-flex min-w-0 flex-1 items-center gap-2 truncate text-left text-sm font-medium text-[#1597ff] hover:underline"
                  onClick={() => {
                    if (file.attachment_id == null) return
                    void openFilePreview(
                      file.attachment_id,
                      uploadedType,
                      file.filename ?? '',
                      applicantId,
                      'CHARGEBACK',
                    )
                  }}
                >
                  <FileText className="h-4 w-4 shrink-0 text-[#1597ff]" aria-hidden />
                  <span className="truncate">
                    {display(file.filename)}
                    {file.type
                      ? ` - ${fileTypes[file.type] ?? ALL_DECISION_FILE_TYPES[file.type] ?? file.type}`
                      : ''}
                    {file.created_at ? ` - ${formatDateTime(file.created_at)}` : ''}
                  </span>
                </button>
                <button
                  type="button"
                  disabled={disabled || busy}
                  onClick={() => {
                    if (file.attachment_id != null) onDelete(file.attachment_id)
                  }}
                  className="shrink-0 rounded-md border border-red-400 bg-white px-3 py-1 text-xs font-semibold text-red-500 transition hover:border-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {typeModalOpen && pendingFiles.length ? (
        <ChargebackFileTypeSelectionModal
          title={`${fieldName === 'decision_file' ? 'Decision' : 'Investigation'} File Upload`}
          files={pendingFiles}
          fileTypes={fileTypes}
          onCancel={resetSelection}
          onSubmit={handleTypesChosen}
        />
      ) : null}

      {uploadConfirmOpen ? (
        <ChargebackConfirmModal
          title="Chargeback Document Upload"
          primaryLabel="Upload"
          onPrimary={() => void confirmUpload()}
          onCancel={resetSelection}
          panelDataTestId="chargeback-upload-confirm-modal"
          primaryDataTestId="chargeback-upload-confirm-submit"
        >
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              Please confirm, really want to upload{' '}
              {pendingFiles.length > 1 ? 'these files' : 'this file'}:
            </p>
            <ul className="space-y-2">
              {pendingFiles.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2.5"
                >
                  <FileText className="h-4 w-4 shrink-0 text-[#1597ff]" aria-hidden />
                  <span
                    className="truncate text-sm font-medium text-slate-800"
                    title={file.name}
                  >
                    {file.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </ChargebackConfirmModal>
      ) : null}
    </div>
  )
}
